import type { WordTiming } from '../../types/roast';
import type { SoundEffectTiming } from './types';

/**
 * Find word timing for a trigger phrase, searching only at/after `startIndex`.
 * First tries to match exact consecutive words; falls back to first single-word
 * match (in trigger-word order). Case-insensitive. Returns BOTH the matched
 * WordTiming (first word of the matched window) AND its absolute index, so callers
 * can advance a monotonic cursor — scoping a repeated trigger phrase to its later
 * occurrence (its own segment's word-timing slice) instead of the global first match.
 */
export function findWordTimingFrom(
  triggerPhrase: string,
  wordTimings: WordTiming[],
  startIndex = 0,
): { timing: WordTiming; index: number } | null {
  const triggerWords = triggerPhrase
    .toLowerCase()
    .split(/\s+/)
    .filter(w => w.length > 0);

  if (triggerWords.length === 0) {
    return null;
  }

  const from = Math.max(0, startIndex);

  for (let i = from; i < wordTimings.length; i++) {
    const windowEndIdx = Math.min(i + triggerWords.length, wordTimings.length);
    const window = wordTimings.slice(i, windowEndIdx);
    const windowWords = window.map(wt => wt.word.toLowerCase());
    if (windowWords.length === triggerWords.length && arraysEqual(windowWords, triggerWords)) {
      return { timing: wordTimings[i], index: i };
    }
  }

  for (const word of triggerWords) {
    for (let i = from; i < wordTimings.length; i++) {
      if (wordTimings[i].word.toLowerCase() === word) {
        return { timing: wordTimings[i], index: i };
      }
    }
  }

  return null;
}

/**
 * Find word timing for a trigger phrase (global first match). Thin wrapper over
 * findWordTimingFrom for the photo path + back-compat callers.
 */
export function findWordTiming(
  triggerPhrase: string,
  wordTimings: WordTiming[],
): WordTiming | null {
  return findWordTimingFrom(triggerPhrase, wordTimings, 0)?.timing ?? null;
}

/**
 * Calculate trigger time based on word timing and timing preference.
 * - before: start time - 100ms (clamped to >= 0)
 * - during: start time
 * - after: end time + 100ms
 *
 * @param timing - Timing preference ('before', 'during', 'after')
 * @param wt - Word timing data
 * @returns Calculated trigger time in seconds
 */
export function calculateTriggerTime(
  timing: SoundEffectTiming,
  wt: WordTiming
): number {
  switch (timing) {
    case 'before':
      return Math.max(0, wt.start - 0.1); // 100ms before, clamped to 0
    case 'during':
      return wt.start;
    case 'after':
      return wt.end + 0.1; // 100ms after
  }
}

/**
 * Helper function to compare two string arrays for equality.
 */
function arraysEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) {
    return false;
  }
  return a.every((val, idx) => val === b[idx]);
}
